/**
 * Offline support for the ST / OT Daily Activity sheets.
 *
 * Both sheets build their rows from two network reads: the child's saved sheet
 * records (the standing goals-config + this session's entry) and the active IEP.
 * With no network either read failed and the sheet opened with no goals, so
 * offline data entry could not start. This module gives both reads a device-side
 * fallback:
 *
 *   - loadSheetRecords: the sheet records, cached per child + sheet after every
 *     successful load and served from that cache when the network is unavailable.
 *     Only what the sheet needs is kept (the goals-config and recent entries), not
 *     the child's full history.
 *   - getLocalSessionEntry: THIS session's entry as saved on the device. Saves go
 *     to IndexedDB first and sync later, so right after an offline save the server
 *     has nothing yet; without this a reload lost what was just entered.
 *
 * Everything here is best-effort and never throws: storage can be blocked
 * (private mode, quota) and a data sheet must still open when it is.
 */
import { getClientDataCollections } from 'api/dataCollection';
import { getOfflineEntries } from 'utils/offlineDB';

// Recent entries kept alongside the goals-config - enough for the goal
// suggestions and a short history offline, small enough to stay well under quota.
const MAX_CACHED_ENTRIES = 20;

const cacheKey = (datasheetTypeCode, childId) => `ayro:dailyActivity:${datasheetTypeCode}:${childId}`;

const byNewest = (a, b) => {
  const ta = a?.collected_at ? new Date(a.collected_at).getTime() : 0;
  const tb = b?.collected_at ? new Date(b.collected_at).getTime() : 0;
  return tb - ta;
};

const writeCache = (key, records) => {
  try {
    const config = records.find((r) => r?.raw_data?.isGoalsConfig === true);
    const entries = records.filter((r) => r?.raw_data?.isGoalsConfig !== true).slice(0, MAX_CACHED_ENTRIES);
    localStorage.setItem(key, JSON.stringify({ cachedAt: new Date().toISOString(), records: config ? [config, ...entries] : entries }));
  } catch {
    // Storage blocked or full - the sheet still works online.
  }
};

const readCache = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    return Array.isArray(parsed?.records) ? parsed.records : [];
  } catch {
    return [];
  }
};

/**
 * The sheet's records, newest first. Network first; on failure, the last copy
 * cached on this device (empty if there is none).
 *
 * @returns {Promise<{records: Array, fromCache: boolean}>}
 */
export const loadSheetRecords = async (childId, datasheetTypeCode) => {
  const key = cacheKey(datasheetTypeCode, childId);
  try {
    const res = await getClientDataCollections(childId, {
      datasheet_type_code: datasheetTypeCode,
      // The server caps page_size at 100. This one call must find the goals-config
      // record as well as the history, so it is not paged down to a screenful.
      page_size: 100
    });
    const records = (res?.items || []).slice().sort(byNewest);
    writeCache(key, records);
    return { records, fromCache: false };
  } catch (err) {
    console.warn(`[dailyActivityOffline] ${datasheetTypeCode} records unavailable, using device copy:`, err?.message);
    return { records: readCache(key).sort(byNewest), fromCache: true };
  }
};

/**
 * This session's entry as saved on the device and NOT yet synced, or null. A
 * synced entry is left to the server copy, which is the one of record.
 *
 * @returns {Promise<Object|null>} a record shaped like a server one ({ raw_data, session_id })
 */
export const getLocalSessionEntry = async (formType, childId, sessionId) => {
  if (!sessionId) return null;
  try {
    const entries = await getOfflineEntries(formType, childId);
    const mine = (entries || [])
      .filter((e) => String(e?.sessionId || '') === String(sessionId))
      .filter((e) => !(e.synced === true || e.syncStatus === 'synced'))
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime());
    const latest = mine[0];
    if (!latest?.data) return null;
    return { session_id: sessionId, collected_at: latest.updatedAt || latest.createdAt, raw_data: latest.data, notes: latest.data.notes };
  } catch {
    return null;
  }
};
